import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type Project = {
  id: number;
  name: string;
  lastActivityAt: string | null;
};

type UseProjectsResult = {
  data: Project[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

export function useProjects(organisationId: number | null): UseProjectsResult {
  const [data, setData] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchProjects = useCallback(async () => {
    if (organisationId == null) {
      setData([]);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const { data: rows, error: err } = await supabase
        .from('project')
        .select('project_id, name, last_activity_at')
        .eq('organisation_id', organisationId)
        .order('last_activity_at', { ascending: false, nullsFirst: false })
        .order('name', { ascending: true });

      if (err) throw err;

      setData(
        (rows ?? []).map((r) => ({
          id: r.project_id,
          name: r.name ?? '',
          lastActivityAt: r.last_activity_at ?? null,
        }))
      );
    } catch (fetchError) {
      setError(fetchError as Error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [organisationId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchProjects,
  };
}
